import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService from "services/annotations.service"
import { useCreateNote } from "hooks/api/notes/notes.hook"
import { insertNoteIntoCache } from "helpers/quotationCache.helper"
import { useNoteToolStore } from "store/noteTool.store"
import { applyNoteToolStyles } from "../core/viewerTools"
import type { TCreateNoteRequest, TNote } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

export type NoteCreateContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	// Leaving editor mode (closing the modal back to preview) disarms the tool — preview has no
	// annotate affordances at all, matching the other canvas modes' own "preview has no selection
	// affordances" effect.
	mode: TViewerMode
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	formatError: (error: unknown) => void
	// Tears down every OTHER canvas mode (connect-objects, the five "add a detection" sidebars)
	// before arming the note tool — every other entry point into a canvas mode unconditionally
	// TAKES OVER from whatever else is open rather than silently no-opping, and arming the note
	// tool must match that (mirrors PR #1381's `useFigureHighlightCreate` for the sibling REQ-400
	// feature).
	cancelOtherCanvasModes: () => void
	// Called once a note is successfully persisted. Apryse's own edit tool auto-selects a
	// freshly-placed FreeText annotation, so `useNoteEdit`'s own selection listener normally picks
	// it up on its own — this hand-off exists for the narrow race it can't cover: if the user types
	// and blurs fast enough that the "modify"/textChanged commit fires BEFORE this create request
	// resolves, that edit finds no tracked note yet (see `useNoteEdit.scheduleCommitForNote`'s own
	// comment) and would otherwise be silently lost. The orchestrator wires this to
	// `scheduleCommitForNote(note.id, annot)` as a one-time catch-up diff pass.
	onNoteCreated?: (note: TNote, annot: Core.Annotations.Annotation) => void
}

// REQ-395/396 note create: arms Apryse's NATIVE `FreeTextAnnotation` click/drag-to-place tool
// (`Tools.ToolNames.FREETEXT`) — genuinely new API surface for this codebase (every other "add X"
// mode uses `useAreaCreate`'s custom backend-rect draft box for a side-panel form; a note has no
// side panel — the annotation itself IS the object, the same category of ask as PR #1381's native
// Rectangle/EllipseAnnotation highlight tool).
//
// Unlike a highlight, the native draft is KEPT on the canvas rather than swapped for an inert
// render pipeline: a note must stay genuinely interactive (native move/resize handles, native
// rich-text editing), so destroying-and-recreating it would kill the user's in-progress typing
// session the instant the box is drawn. Instead the draft is POSTed immediately (mirroring the
// highlight create flow's "detect placement, POST, hand off" shape), sending back whatever text is
// ALREADY on the annotation the instant it's placed (Apryse seeds a default placeholder — see
// `handleAnnotationAdded`'s own comment) — the user is expected to keep typing into the SAME
// annotation right after, and `useNoteEdit`'s generic diff-and-commit path (bound once the note is
// tracked) picks up further typed text on its own via the standard `annotationChanged`/"modify"
// listener, exactly as it would for editing an existing note. This avoids a separate "PATCH right
// after create just to set the text" path.
//
// Primary-pane only, matching `core/viewerTools.ts`'s `toggleRulerTool` precedent (not
// `setToolModeOnAllPanes`): notes are NOT mirrored into the split pane (out of scope, see
// AGENTS.md's note-tool section and issue #1321 §7), so there is nothing for a second pane's own
// FreeText tool to hand off to — arming it there would only risk a note landing in the split
// manager, which nothing here tracks or persists.
export const useNoteCreate = (ctx: NoteCreateContext) => {
	const { instanceRef, mode, numericQuotationIdRef, queryClient, formatError, cancelOtherCanvasModes, onNoteCreated } =
		ctx

	const isArmed = useNoteToolStore((state) => state.isArmed)
	const armNoteToolStore = useNoteToolStore((state) => state.armNoteTool)
	const disarmNoteToolStore = useNoteToolStore((state) => state.disarmNoteTool)

	const { mutateAsync: createNoteAsync } = useCreateNote()

	// Read inside the tool's event handler, which is bound once per arm and must not go stale.
	const selectedFontSizeRef = useRef(useNoteToolStore.getState().selectedFontSize)
	const selectedTextColorRef = useRef(useNoteToolStore.getState().selectedTextColor)
	useEffect(
		() =>
			useNoteToolStore.subscribe((state) => {
				selectedFontSizeRef.current = state.selectedFontSize
				selectedTextColorRef.current = state.selectedTextColor
				// Keep the native create tool's own defaults in step with the pickers, so the annotation
				// (and therefore the inline editor Apryse opens on it) is born at the chosen size rather
				// than at Apryse's stock 12pt — see `applyNoteToolStyles`. Subscribing rather than
				// re-running on render for the same reason the refs above exist: the store is read from a
				// listener bound once per arm.
				applyNoteToolStyles(instanceRef.current, {
					fontSize: state.selectedFontSize,
					textColor: state.selectedTextColor,
				})
			}),
		[instanceRef],
	)

	// Returns whether it actually disarmed anything, so the Escape hotkey chain (onCancelCanvasMode)
	// knows to stop here instead of falling through — same contract as the other cancelCreateX()
	// functions in that chain.
	const disarmNoteTool = useCallback((): boolean => {
		const wasArmed = useNoteToolStore.getState().isArmed
		if (wasArmed) disarmNoteToolStore()
		// Reset even when the flag was ALREADY clear: Apryse can sit on FREETEXT independently of our
		// store, and the entity mousedown handler requires EDIT before it will hit-test anything —
		// leaving it stuck is what made erefs/ecalls/furniture unselectable. Gated on FREETEXT
		// actually being active so this never clobbers pan/zoom/ruler.
		const instance = instanceRef.current
		if (instance && mode === "editor") {
			const { documentViewer, Tools } = instance.Core
			if (documentViewer.getToolMode() === documentViewer.getTool(Tools.ToolNames.FREETEXT)) {
				documentViewer.setToolMode(documentViewer.getTool(Tools.ToolNames.EDIT))
			}
		}
		return wasArmed
	}, [instanceRef, disarmNoteToolStore, mode])

	const armNoteTool = useCallback(() => {
		const instance = instanceRef.current
		if (!instance) return
		// Always takes over — matches every other canvas-mode entry point, none of which silently
		// no-op when something else is open.
		cancelOtherCanvasModes()
		const { documentViewer, Tools, getDocumentViewers } = instance.Core
		armNoteToolStore()
		// Seed the create tool BEFORE it is armed, so the very first note drawn in this session already
		// carries the bar's current font size / colour (the store subscription above only fires on a
		// CHANGE, and the user may draw without touching either picker).
		applyNoteToolStyles(instance, {
			fontSize: selectedFontSizeRef.current,
			textColor: selectedTextColorRef.current,
		})
		documentViewer.setToolMode(documentViewer.getTool(Tools.ToolNames.FREETEXT))
		// Primary-pane only (see the module comment) — leave any split pane on the plain select tool
		// so a click there can't start a second, untracked FreeText draft.
		const viewers = getDocumentViewers?.() ?? [documentViewer]
		for (const viewer of viewers.slice(1)) {
			viewer?.setToolMode(viewer.getTool(Tools.ToolNames.EDIT))
		}
	}, [instanceRef, cancelOtherCanvasModes, armNoteToolStore])

	const handleAnnotationAdded = useCallback(
		async (annot: Core.Annotations.Annotation) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) {
				// Bail out (and drop the native draft) if there's no quotation to create against —
				// should not happen in editor mode, but guards a race on close.
				AnnotationService.discardDraftNoteAnnotation(annot)
				return
			}

			// Apryse's FREETEXT tool does NOT revert to EDIT on its own after a placement. Disarming
			// the store alone left the real tool mode stuck, and the entity mousedown handler requires
			// EDIT to hit-test — so nothing but notes could be selected afterwards. `disarmNoteTool()`
			// resets both.
			disarmNoteTool()

			const { pageNumber, coordinates } = AnnotationService.getBackendRectFromAnnotation(annot)
			// Read whatever is ALREADY on the annotation the instant it was placed — live verification
			// (this session, against a running instance) showed Apryse seeds every new FreeText with
			// its own default placeholder text ("Insert text here"), not an empty string, so sending
			// that back keeps the create request byte-identical to what's already on screen instead of
			// drifting until the user's first blur re-syncs it via the SAME diff-and-commit path an
			// edit uses (`useNoteEdit`).
			const body: TCreateNoteRequest = {
				...coordinates,
				page_number: pageNumber,
				text: annot.getContents(),
				font_size: selectedFontSizeRef.current,
				text_color: selectedTextColorRef.current,
			}

			try {
				const { data: created } = await createNoteAsync({ quotationId: qid, body })
				// Keep the SAME native draft annotation (unlike the highlight flow, which swaps its
				// draft for an inert render) so the user's in-progress rich-text-editor session isn't
				// interrupted the instant the box is drawn.
				AnnotationService.registerCreatedNoteAnnotation(annot, created)
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
					insertNoteIntoCache(cached, created),
				)
				onNoteCreated?.(created, annot)
			} catch (error) {
				AnnotationService.discardDraftNoteAnnotation(annot)
				formatError(error)
			}
		},
		[numericQuotationIdRef, createNoteAsync, queryClient, formatError, onNoteCreated, disarmNoteTool],
	)

	// Detect the native create tool finishing a placement. Goes through the ANNOTATION MANAGER's own
	// "annotationChanged" event rather than the tool instance's own `.on()` emitter — this
	// codebase's own hard-won precedent (see AGENTS.md's "Custom WebViewer" section / PR #1381's
	// `handleAnnotationAdded` comment) is that a WebViewer 11.9.1 create-tool object can come back
	// without usable `.on`/`.off` methods even on a fully-initialized viewer, so binding through
	// `annotationManager.addEventListener` (an object this codebase already round-trips through
	// reliably) avoids depending on that. Apryse fires "annotationChanged" with action "add" for
	// EVERY `addAnnotations` call, including ones this service triggers itself (`addNote`,
	// `renderNotes`), so the filter has to positively identify "a fresh, UNTRACKED FreeTextAnnotation"
	// rather than just "any annotation showed up" — `isOwnNoteAnnotation` covers that the same way
	// `isOwnAnnotation` does for highlights.
	//
	// Primary-pane only (see the module comment): bound to `instance.Core.documentViewer`'s own
	// manager, never a split-pane manager.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance || !isArmed) return
		const { Annotations, documentViewer } = instance.Core
		const annotationManager = documentViewer.getAnnotationManager()
		if (!annotationManager) return

		const onChanged = (annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => {
			if (action !== "add" || info?.imported) return
			for (const annotation of annotations) {
				if (!(annotation instanceof Annotations.FreeTextAnnotation)) continue
				if (AnnotationService.isOwnNoteAnnotation(annotation.Id)) continue
				void handleAnnotationAdded(annotation)
			}
		}
		annotationManager.addEventListener("annotationChanged", onChanged)
		return () => annotationManager.removeEventListener("annotationChanged", onChanged)
	}, [instanceRef, isArmed, handleAnnotationAdded])

	// Safety net for "the tool never got disarmed". If `isArmed` sticks, `isCanvasModeActive()` keeps
	// reporting a live canvas mode and every later click on an eref/ecall/furniture is dropped before
	// the hit-test — the editor looks like selection is broken. `handleAnnotationAdded` is the primary
	// disarm and, measured on this build, it does run for a plain click too ("add" fires when the
	// inline editor opens), so this listener normally arrives later as a no-op. It is kept because it
	// costs nothing, it was observed firing, and disarming twice is harmless — the failure it guards
	// against is silent and expensive to diagnose.
	//
	// Which emitter API this manager exposes varies by build — `.on`/`.off` are in the typings but
	// missing at runtime here (the same gap AGENTS.md documents for this SDK's tool objects), and
	// calling `.on` blindly threw during arming and took the whole editor down (PR #1386 review).
	// So feature-detect, prefer the `addEventListener` pair this codebase already relies on
	// elsewhere, and bind nothing at all rather than throw if neither exists.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance || !isArmed) return
		type TEmitter = {
			addEventListener?: (event: string, cb: () => void) => void
			removeEventListener?: (event: string, cb: () => void) => void
			on?: (event: string, cb: () => void) => void
			off?: (event: string, cb: () => void) => void
		}
		const editBoxManager = instance.Core.annotationManager.getEditBoxManager?.() as TEmitter | undefined
		if (!editBoxManager) return
		const add = editBoxManager.addEventListener ?? editBoxManager.on
		const remove = editBoxManager.removeEventListener ?? editBoxManager.off
		if (typeof add !== "function") return
		const onEditorBlur = () => disarmNoteTool()
		add.call(editBoxManager, "editorBlur", onEditorBlur)
		return () => {
			if (typeof remove === "function") remove.call(editBoxManager, "editorBlur", onEditorBlur)
		}
	}, [instanceRef, isArmed, disarmNoteTool])

	// Preview mode has no annotate affordances at all — leave the tool armed and it would still be
	// armed (and the tool-options bar still showing) the next time the modal opens in editor mode.
	useEffect(() => {
		if (mode !== "editor") disarmNoteTool()
	}, [mode, disarmNoteTool])

	return {
		isArmed,
		armNoteTool,
		disarmNoteTool,
	}
}
